"use client"

import { useMemo } from "react"
import dynamic from "next/dynamic"

import { startCompletion } from "@codemirror/autocomplete"
import {
  PostgreSQL,
  sql,
  SQLDialect,
  type SQLNamespace,
} from "@codemirror/lang-sql"
import { syntaxTree } from "@codemirror/language"
import { linter, lintGutter, type Diagnostic } from "@codemirror/lint"
import { EditorView } from "@codemirror/view"
import { Button } from "@coval/ui"
import { type ViewUpdate } from "@uiw/react-codemirror"
import { Database } from "lucide-react"
import { useTheme } from "next-themes"

import { editorTypographyTheme } from "./codeMirrorTheme"
import type { SchemaCompletion } from "./SqlSchemaPanel"

// The editor view needs the DOM, so load it client-side only (mirrors how
// JsonEditor lazy-loads Monaco).
const CodeMirror = dynamic(() => import("@uiw/react-codemirror"), {
  ssr: false,
})

const CLICKHOUSE_KEYWORDS =
  "all anti any array asof final format global join prewhere sample semi settings totals"

const CLICKHOUSE_BUILTINS =
  "avgIf countIf JSONExtract JSONExtractString quantile quantileIf sumIf toDateTime toStartOfDay toStartOfHour toStartOfInterval uniq uniqExact"

const CLICKHOUSE_TYPES =
  "Array Date Date32 DateTime DateTime64 Decimal Enum8 Enum16 FixedString Float32 Float64 Int8 Int16 Int32 Int64 Int128 Int256 IPv4 IPv6 LowCardinality Map Nullable Object String Tuple UInt8 UInt16 UInt32 UInt64 UInt128 UInt256 UUID"

// CodeMirror has no ClickHouse dialect, so extend its PostgreSQL parser with the ClickHouse clauses, functions, types, and identifier quoting used by Custom SQL metrics.
const CLICKHOUSE_DIALECT = SQLDialect.define({
  ...PostgreSQL.spec,
  identifierQuotes: '"`',
  operatorChars: "+-*/<>=~!@#%^&|?",
  keywords: `${PostgreSQL.spec.keywords ?? ""} ${CLICKHOUSE_KEYWORDS}`,
  builtin: `${PostgreSQL.spec.builtin ?? ""} ${CLICKHOUSE_BUILTINS}`,
  types: `${PostgreSQL.spec.types ?? ""} ${CLICKHOUSE_TYPES}`,
})

// Clause keywords after which we auto-open the completion list, so suggestions
// (tables/columns) appear right after `SELECT `, `FROM `, `WHERE `, etc. without
// needing an explicit Ctrl-Space. lang-sql otherwise stays quiet at the empty
// position following a keyword.
const CLAUSE_KEYWORDS = new Set([
  "select",
  "from",
  "where",
  "and",
  "or",
  "on",
  "join",
  "group",
  "order",
  "by",
  "having",
])

/**
 * Opens the completion popup when the user just typed a space after a clause
 * keyword. Deferred to the next tick because a transaction can't be dispatched
 * from within an update.
 */
function autoCompleteAfterClauseKeyword(update: ViewUpdate): void {
  if (!update.docChanged) {
    return
  }
  const isUserInput = update.transactions.some((transaction) =>
    transaction.isUserEvent("input.type"),
  )
  if (!isUserInput) {
    return
  }
  const cursor = update.state.selection.main.head
  const textBeforeCursor = update.state.sliceDoc(0, cursor)
  const keywordMatch = /([A-Za-z_]+)\s+$/.exec(textBeforeCursor)
  if (!keywordMatch || !CLAUSE_KEYWORDS.has(keywordMatch[1].toLowerCase())) {
    return
  }
  window.setTimeout(() => startCompletion(update.view), 0)
}

// Boost applied to schema completions so the metric's own tables/columns rank
// above the dialect's keyword list (which lang-sql gives a boost of -1).
const SCHEMA_COMPLETION_BOOST = 99

/**
 * Returns the schema with a positive `boost` on every leaf completion, so
 * schema entries sort to the top of the completion list ahead of SQL keywords.
 */
function boostSchema(namespace: SQLNamespace): SQLNamespace {
  if (Array.isArray(namespace)) {
    return (namespace as readonly SchemaCompletion[]).map((entry) =>
      typeof entry === "string"
        ? { label: entry, boost: SCHEMA_COMPLETION_BOOST }
        : { ...entry, boost: entry.boost ?? SCHEMA_COMPLETION_BOOST },
    )
  }
  // The `{ self, children }` form carries its own completions; leave it as-is.
  if ("self" in namespace) {
    return namespace
  }
  return Object.fromEntries(
    Object.entries(namespace as Record<string, SQLNamespace>).map(
      ([name, child]) => [name, boostSchema(child)],
    ),
  )
}

// Cap reported diagnostics so a pathological parse can't flood the editor with
// markers; a handful is enough to point the user at the problem.
const MAX_SYNTAX_DIAGNOSTICS = 20

/** Reports parser errors in the editor; semantic failures such as unknown columns or an invalid result shape surface when the query is tested against recent simulations. */
const sqlSyntaxLinter = linter((view) => {
  const diagnostics: Diagnostic[] = []
  const docLength = view.state.doc.length
  syntaxTree(view.state)
    .cursor()
    .iterate((node) => {
      if (!node.type.isError || diagnostics.length >= MAX_SYNTAX_DIAGNOSTICS) {
        return
      }
      const from = node.from
      const to = node.to > from ? node.to : Math.min(from + 1, docLength)
      if (to <= from) {
        return
      }
      diagnostics.push({
        from,
        to,
        severity: "error",
        message: "Unexpected SQL syntax here.",
      })
    })
  return diagnostics
})

interface SqlEditorProps {
  /** The SQL query text. */
  value: string
  /** Called with the new query text on every edit. */
  onChange: (value: string) => void
  /** Placeholder shown when the editor is empty. */
  placeholder?: string
  /** Editor height (default: "200px"). */
  height?: string
  /** Optional className for the container. */
  className?: string
  /** Accessible label for the editor. */
  ariaLabel: string
  /** Whether the editor is read-only. */
  readOnly?: boolean
  /**
   * Table/column schema used to bias autocomplete: a map of table name to its
   * columns. When given, the editor completes table names and, after `table.`,
   * that table's columns. Schema entries are boosted above SQL keywords.
   */
  schema?: SQLNamespace
  /**
   * Called when the footer's schema link is clicked. When provided, the footer
   * renders the link; the consumer owns where the schema reference is shown.
   */
  onToggleSchemaPanel?: () => void
  /** Whether the schema reference is currently shown (labels the footer link). */
  isSchemaPanelOpen?: boolean
}

/** A client-only CodeMirror SQL editor with ClickHouse-aware syntax, linting, schema autocomplete, and an optional schema-panel control. */
export function SqlEditor({
  value,
  onChange,
  placeholder,
  height = "200px",
  className = "",
  ariaLabel,
  readOnly = false,
  schema,
  onToggleSchemaPanel,
  isSchemaPanelOpen = false,
}: SqlEditorProps) {
  const { theme } = useTheme()
  const extensions = useMemo(
    () => [
      sql({
        dialect: CLICKHOUSE_DIALECT,
        schema: schema && boostSchema(schema),
      }),
      sqlSyntaxLinter,
      lintGutter(),
      editorTypographyTheme,
      // @uiw spreads unrecognized props onto its wrapper div, which carries no
      // role, so the name has to be put on the content element itself.
      EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
    ],
    [schema, ariaLabel],
  )

  const setup = useMemo(
    () => ({ foldGutter: false, highlightActiveLine: false }),
    [],
  )

  return (
    <div className={className}>
      <div className="overflow-hidden rounded border border-coval-border">
        <CodeMirror
          value={value}
          onChange={onChange}
          onUpdate={autoCompleteAfterClauseKeyword}
          height={height}
          theme={theme === "dark" ? "dark" : "light"}
          extensions={extensions}
          placeholder={placeholder}
          readOnly={readOnly}
          // Tab moves focus out of the editor like any other form field, so
          // keyboard users are never trapped inside it.
          indentWithTab={false}
          basicSetup={setup}
        />
      </div>
      {onToggleSchemaPanel && (
        <div className="mt-1 flex">
          <Button
            type="button"
            variant="ghost"
            className="h-auto px-2 py-1 text-xs text-coval-text-muted"
            onClick={onToggleSchemaPanel}
            aria-expanded={isSchemaPanelOpen}
          >
            <Database />
            {isSchemaPanelOpen ? "Hide schema" : "View schema"}
          </Button>
        </div>
      )}
    </div>
  )
}
